import {
  ApolloClient,
  ApolloQueryResult,
  gql,
  NormalizedCacheObject,
} from '@apollo/client'
import type { GetPublicProfilePrefetchQuery } from 'apollo-gql'
import allFeatureFlagsFragment from 'graphql/fragments/allFeatureFlagsFragment'
import featuredVideosFragment from 'graphql/fragments/featuredVideosFragment'
import fullPageFragment from 'graphql/fragments/fullPageFragment'
import inProfileCollectionsFragment from 'graphql/fragments/inProfileCollectionsFragment'
import profileRequestOccasionsFragment from 'graphql/fragments/profileRequestOccasionsFragment'
import sideMenuFragment from 'graphql/fragments/sideMenuFragment'
import { currentLanguageIdVar, currentMarketIdVar } from 'graphql/reactive'

import { POTENTIAL_PROFILE_FRAGMENT } from 'hooks/usePublicPotentialProfile'
import { PROFILE_FRAGMENT } from 'hooks/usePublicProfile'

const GET_PUBLIC_PROFILE_PREFETCH = gql`
  query GetPublicProfilePrefetch(
    $marketId: ID!
    $languageId: String!
    $slug: String!
    $pageSlug: String!
    $profilesPerCollection: Int! = 7
    $requestOccasionFirst: Int = 25
    $productType: ParentProductType = VIDEO_REQUEST
  ) {
    market(id: $marketId) {
      id
      profile(slug: $slug) {
        ...fullPublicProfile
        ...inProfileCollectionsFragment
        ...featuredVideosFragment
      }
      potentialProfile(slug: $slug) {
        ...fullPublicPotentialProfile
      }
      page(languageId: $languageId, slug: $pageSlug) {
        ...fullPageFragment
      }

      ...profileRequestOccasionsFragment

      ...sideMenuFragment
    }
    ...allFeatureFlagsFragment
  }
  ${PROFILE_FRAGMENT}
  ${POTENTIAL_PROFILE_FRAGMENT}
  ${inProfileCollectionsFragment}
  ${fullPageFragment}
  ${featuredVideosFragment}
  ${sideMenuFragment}
  ${allFeatureFlagsFragment}
  ${profileRequestOccasionsFragment}
`

async function prefetchProfilePage(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  variables: { slug: string | string[] },
): Promise<ApolloQueryResult<GetPublicProfilePrefetchQuery> | undefined> {
  const marketId = currentMarketIdVar()
  const languageId = currentLanguageIdVar()

  if (marketId && languageId) {
    const { slug } = variables
    const pageSlug = `profile-page-${slug}`
    const profile = await apolloClient.query<GetPublicProfilePrefetchQuery>({
      query: GET_PUBLIC_PROFILE_PREFETCH,
      variables: {
        marketId,
        languageId,
        pageSlug,
        ...variables,
      },
    })

    return profile
  }

  return undefined
}

export default prefetchProfilePage
